/**
 * The bank apps «К оплате» can open on a transfer by phone number, with the driver's
 * number already in it. The amount is never handed over: the passenger types it in
 * the bank app, so what leaves for the driver is what they typed and checked there,
 * not a figure filled in for them.
 *
 * None of these links is published by its bank. They were found in the apps
 * themselves and checked on a handset on 2026-09-24: Т-Банк and СберБанк open the
 * form with the number filled in, and on 2026-10-01 with the amount left empty. The
 * ВТБ link was read out of ВТБ Онлайн 20.17 and the Ozon Банк one out of Ozon Банк
 * 19.31.0; neither was ever seen working. The mobile app's
 * `apps/rider/lib/core/banks.dart` is the same list.
 *
 * A page can only reach them on Android, through an `intent:` link that names the
 * bank's package: the same link is claimed by others too — `android-app://` by the
 * Google app, Sber's https by every browser. Nothing here pays anybody; the passenger
 * confirms in the bank app, and coming back says nothing about whether they did
 * (ADR 0004).
 */

export type Bank = 'sber' | 'tbank' | 'vtb' | 'ozon';

export const BANKS: readonly Bank[] = ['sber', 'tbank', 'vtb', 'ozon'];

export const BANK_LABEL: Record<Bank, string> = {
  sber: 'СберБанк',
  tbank: 'Т-Банк',
  vtb: 'ВТБ',
  ozon: 'Ozon Банк',
};

const PACKAGE: Record<Bank, string> = {
  sber: 'ru.sberbankmobile',
  tbank: 'com.idamob.tinkoff.android',
  vtb: 'ru.vtb24.mobilebanking.android',
  ozon: 'ru.ozon.fintech.finance',
};

/** `7` and ten digits — the one form all four take. */
function digits(phone: string): string {
  let out = phone.replace(/\D/g, '');
  if (out.length === 11 && out.startsWith('8')) out = `7${out.slice(1)}`;
  if (out.length === 10) out = `7${out}`;
  return out;
}

/**
 * The `intent:` link that opens [bank]'s transfer form, in that bank's app alone,
 * with the amount left to the passenger.
 */
export function bankLink(bank: Bank, phone: string): string {
  const number = digits(phone);
  const [scheme, rest] = ((): [string, string] => {
    switch (bank) {
      // The route Samsung Pay hands over to. Both parameters are required: without
      // either, it opens nothing. An empty amount is still one, and leaves the field
      // to the passenger.
      case 'sber':
        return [
          'https',
          `online.sberbank.ru/app/payments/samsungpay?ReceiverPhone=${number}&buyAmount=`,
        ];
      // `bank100000000004://` has the same route and does not open it.
      case 'tbank':
        return ['tbank', `Main/PayByMobileNumber?numberPhone=${number}`];
      // Every digit after `ppl/` becomes the number, so nothing may follow it.
      case 'vtb':
        return ['vtb', `online.vtb.ru/i/ppl/${number}`];
      // The page Ozon Банк's own «Перевести деньги по этому номеру телефона» entry
      // turns a number into. That entry is a `tel:` link, which the mobile app sends
      // and a browser may keep for the dialer.
      case 'ozon':
        return ['https', `finance.ozon.ru/m/lk/payments?pn=${number}`];
    }
  })();
  return `intent://${rest}#Intent;scheme=${scheme};package=${PACKAGE[bank]};end`;
}

/**
 * Whether this browser can hand an `intent:` link to an app.
 *
 * It cannot say which bank apps are installed — no browser tells a page what else is
 * on the phone — so on Android all four get a tile, where the mobile app shows only
 * the ones it finds. A tile for a bank that is not there opens the phone's store on
 * it instead.
 */
export function opensBankApps(userAgent: string = navigator.userAgent): boolean {
  return /Android/.test(userAgent);
}
